import Client from "fhir-kit-client";
import { Bundle, Organization } from "fhir/r5";

export type OrganizationNode = {
    id: string;
    name: string;
    reference: string;
    parentReference?: string;
    children: OrganizationNode[];
};

export interface OrganizationServiceConfig {
    fhirBaseUrl: string;
}

class OrganizationService {
    private client: Client;

    constructor(config: OrganizationServiceConfig) {
        this.client = new Client({
            baseUrl: config.fhirBaseUrl
        });
    }

    /**
     * Recherche une organisation à partir d'un identifiant métier.
     *
     * Le POC utilise cette méthode pour retrouver le pôle configuré dans la page
     * Vue d'ensemble. La valeur peut également correspondre à l'id technique de
     * la ressource : un repli sur une lecture directe est alors réalisé.
     */
    async getOrganizationByIdentifier(identifier: string): Promise<OrganizationNode> {
        const bundle = await this.client.search({
            resourceType: "Organization",
            searchParams: {
                identifier,
                _count: 2
            }
        }) as Bundle<Organization>;

        const organization = this.extractOrganizations(bundle)[0];

        if (organization) {
            return this.toOrganizationNode(organization);
        }

        try {
            const resource = await this.client.read({
                resourceType: "Organization",
                id: identifier
            }) as Organization;

            if (resource.resourceType === "Organization" && resource.id) {
                return this.toOrganizationNode(resource);
            }
        } catch (error) {
            console.error("Impossible de lire le pôle par son identifiant technique.", error);
        }

        throw new Error(`Aucun pôle trouvé pour l'identifiant ${identifier}.`);
    }

    /**
     * Retourne les organisations directement rattachées à une organisation mère.
     */
    async getChildOrganizations(parentReference: string): Promise<OrganizationNode[]> {
        const bundle = await this.client.search({
            resourceType: "Organization",
            searchParams: {
                partof: parentReference,
                _count: 100,
                _sort: "name"
            }
        }) as Bundle<Organization>;

        return this.extractOrganizations(bundle)
            .filter((organization) => organization.active !== false)
            .map((organization) => this.toOrganizationNode(organization));
    }

    async searchInternalOrganizationTree(): Promise<OrganizationNode[]> {
        const bundle = await this.client.search({
            resourceType: "Organization",
            searchParams: {
                _count: 100,
                _tag: "internal",
                identifier: "http://chun.upcare.fr/fhir/sid/service|",
                _sort: "name"
            }
        }) as Bundle<Organization>;

        const organizations = this.extractOrganizations(bundle);

        return this.buildOrganizationTree(organizations);
    }

    private extractOrganizations(bundle: Bundle<Organization>): Organization[] {
        return (
            bundle.entry
                ?.map((entry) => entry.resource)
                .filter(
                    (resource): resource is Organization =>
                        !!resource &&
                        resource.resourceType === "Organization" &&
                        !!resource.id
                ) ?? []
        );
    }

    private buildOrganizationTree(
        organizations: Organization[]
    ): OrganizationNode[] {
        const nodes = organizations.map((organization) =>
            this.toOrganizationNode(organization)
        );

        const nodeByReference = new Map(
            nodes.map((node) => [node.reference, node])
        );

        nodes.forEach((node) => {
            if (!node.parentReference) {
                return;
            }

            const parent = nodeByReference.get(node.parentReference);

            if (parent) {
                parent.children.push(node);
            }
        });

        return nodes.filter((node) => !node.parentReference);
    }

    private toOrganizationNode(organization: Organization): OrganizationNode {
        const id = organization.id ?? "";

        return {
            id,
            name: organization.name ?? id,
            reference: `Organization/${id}`,
            parentReference: this.getParentReference(organization),
            children: []
        };
    }

    private getParentReference(
        organization: Organization
    ): string | undefined {
        if (organization.partOf?.reference) {
            return organization.partOf.reference;
        }

        if (organization.partOf?.identifier?.value) {
            return `Organization/${organization.partOf.identifier.value}`;
        }

        return undefined;
    }
}

export default OrganizationService;
